import React, { useState, useEffect } from 'react';
import { Plus, Calendar as CalendarIcon, List, Award, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import HabitList from './HabitList';
import HabitForm from './HabitForm';
import CalendarView from './CalendarView';
import StatsView from './StatsView';
import GamificationBar from './GamificationBar';
import BadgesList, { BADGE_DEFINITIONS } from './BadgesList';
import InsightsCard from './InsightsCard';
import Onboarding from './Onboarding';
import ReminderNotifications from './ReminderNotifications';

import UpcomingReminders from './UpcomingReminders';
import HorizontalCalendar from './HorizontalCalendar';
import { SUGGESTED_HABITS, ICON_MAP } from '../lib/habitTemplates';

const HabitTracker = ({ session }) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [habits, setHabits] = useState([]);
  const [completions, setCompletions] = useState([]);

  const [profile, setProfile] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState('choice'); // 'choice', 'library', 'form'
  const [editingHabit, setEditingHabit] = useState(null);

  // States for gamification (these should ideally be in profile table)
  const [points, setPoints] = useState(0);
  const [badges, setBadges] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Default tab logic: Mobile users start on 'stats' to see their progress immediately
  const [defaultTab] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768 ? 'stats' : 'today';
    }
    return 'today';
  });

  // Load Initial Data
  // Load Initial Data
  useEffect(() => {
    const syncSubscription = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) return null;

        const response = await fetch('/api/sync-subscription', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentSession.access_token}`
          }
        });

        if (response.ok) {
          // After sync, fetch profile again from Supabase as the backend created it
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
          return data;
        }
      } catch (e) {
        console.error("Sync failed", e);
      }
      return null;
    };

    const fetchData = async () => {
      try {
        if (!session?.user) return;

        // 1. Fetch Habits
        const { data: habitsData } = await supabase
          .from('habits')
          .select('*')
          .order('created_at');


        if (habitsData) {
          const formattedHabits = habitsData.map(h => ({
            ...h,
            name: h.title, // Map title -> name for frontend
            ...h.frequency // Spread frequency metadata (color, icon, etc) to top level
          }));
          setHabits(formattedHabits);
        } else if (!localStorage.getItem('hasSeenOnboarding')) {
          setShowOnboarding(true);
        }

        // 2. Fetch Completions
        const { data: completionsData } = await supabase
          .from('habit_completions')
          .select('*');

        if (completionsData) {
          // Transform for frontend compatibility if needed
          const formattedCompletions = completionsData.map(c => ({
            ...c,
            habitId: c.habit_id,
            date: c.completed_at
          }));
          setCompletions(formattedCompletions);
        }

        // 3. Fetch Profile
        let { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (!profileData) {
          // Profile missing? Try to sync with backend to create it/restore purchase
          console.log("Profile missing, attempting to sync with backend...");
          profileData = await syncSubscription();
        }


        if (profileData) {
          setProfile(profileData);
          // Load points from profile OR localStorage as fallback
          const savedPoints = parseInt(localStorage.getItem('userPoints'), 10) || 0;
          setPoints(profileData.points || savedPoints);

          // Load badges from profile
          if (profileData.badges && Array.isArray(profileData.badges)) {
            setBadges(profileData.badges);
          }
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [session]);


  const handleOnboardingComplete = async (selectedHabits) => {
    const newHabits = selectedHabits.map(habit => ({
      user_id: session.user.id,
      title: habit.name,
      description: `Meta: ${habit.target} ${habit.unit}`,
      frequency: {
        category: habit.category,
        color: habit.color,
        icon: habit.icon,
        target: habit.target,
        unit: habit.unit,
        type: 'daily'
      },
    }));

    const { data, error } = await supabase.from('habits').insert(newHabits).select();

    if (data) {
      // Transform back for UI state
      const formattedHabits = data.map(h => ({
        ...h,
        name: h.title,
        ...h.frequency
      }));
      setHabits(formattedHabits);
    }

    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };


  const addHabit = async (habit) => {
    // Correctly map form fields to database schema
    const newHabit = {
      user_id: session.user.id,
      title: habit.name, // The form returns 'name', DB expects 'title'
      description: habit.description,
      // Map other form fields into 'frequency' JSONB or dedicated columns if you added them
      // For now, storing extra metadata in frequency is a common flexible approach if columns don't exist
      frequency: {
        category: habit.category,
        color: habit.color,
        icon: habit.icon,
        target: habit.target,
        unit: habit.unit,
        reminderTime: habit.reminderTime,
        reminderFrequency: habit.reminderFrequency,
        reminderInterval: habit.reminderInterval,
        reminderInterval: habit.reminderInterval,
        type: habit.frequencyType || 'daily',
        weekDays: habit.weekDays,
        monthDay: habit.monthDay
      },
    };

    const { data, error } = await supabase.from('habits').insert([newHabit]).select();


    if (data) {
      const formattedHabit = {
        ...data[0],
        name: data[0].title,
        ...data[0].frequency
      };
      setHabits([...habits, formattedHabit]);
      setIsDialogOpen(false);
    } else {
      console.error(error);
    }
  };


  const updateHabit = async (updatedHabit) => {
    const { data, error } = await supabase
      .from('habits')
      .update({
        title: updatedHabit.name, // The form returns 'name', DB expects 'title'
        description: updatedHabit.description,
        frequency: {
          category: updatedHabit.category,
          color: updatedHabit.color,
          icon: updatedHabit.icon,
          target: updatedHabit.target,
          unit: updatedHabit.unit,
          reminderTime: updatedHabit.reminderTime,
          reminderFrequency: updatedHabit.reminderFrequency,
          reminderInterval: updatedHabit.reminderInterval,
          reminderInterval: updatedHabit.reminderInterval,
          type: updatedHabit.frequencyType || 'daily',
          weekDays: updatedHabit.weekDays,
          monthDay: updatedHabit.monthDay
        }
      })
      .eq('id', updatedHabit.id)
      .select();


    if (data) {
      const formattedHabit = {
        ...data[0],
        name: data[0].title,
        ...data[0].frequency
      };
      setHabits(habits.map(h => h.id === updatedHabit.id ? formattedHabit : h));
      setEditingHabit(null);
      setIsDialogOpen(false);
    }
  };

  const deleteHabit = async (habitId) => {
    const { error } = await supabase.from('habits').delete().eq('id', habitId);

    if (!error) {
      setHabits(habits.filter(h => h.id !== habitId));
      setCompletions(completions.filter(c => c.habitId !== habitId));
    }
  };





  const updatePoints = async (newPoints) => {
    setPoints(newPoints);
    localStorage.setItem('userPoints', newPoints); // Save locally immediately
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ points: newPoints })
        .eq('id', session.user.id);

      if (error) {
        // Prepare for missing column scenario
        if (error.code === 'PGRST204' || error.message.includes("Could not find the 'points' column")) {
          console.warn("Points column missing in Supabase. Using local storage only.");
          return;
        }
        throw error;
      }
    } catch (err) {
      console.error('Error updating points:', err);
    }
  };

  const toggleCompletion = async (habitId, date, value = null) => {
    const dateStr = date.toISOString().split('T')[0];
    const existingCompletion = completions.find(
      c => c.habitId === habitId && c.date === dateStr
    );

    if (value !== null) {
      // Mode: Update/Set Value (for quantitative habits)
      if (existingCompletion) {
        // Update existing (no point change for just changing value for now, unless we want to?)
        const { data, error } = await supabase
          .from('habit_completions')
          .update({ value: value })
          .eq('id', existingCompletion.id)
          .select();

        if (data) {
          const updatedComp = { ...data[0], habitId: data[0].habit_id, date: data[0].completed_at };
          setCompletions(completions.map(c => c.id === existingCompletion.id ? updatedComp : c));
        }
      } else {
        // Insert new with value
        const { data, error } = await supabase
          .from('habit_completions')
          .insert([{
            user_id: session.user.id,
            habit_id: habitId,
            completed_at: dateStr,
            value: value
          }])
          .select();

        if (data) {
          const newCompletion = { ...data[0], habitId: data[0].habit_id, date: data[0].completed_at };
          const newCompletionsStart = [...completions, newCompletion];
          setCompletions(newCompletionsStart);

          const newPoints = points + 10;
          updatePoints(newPoints);

          checkAndAwardBadges(newCompletionsStart, newPoints);
        }
      }

    } else {
      // Mode: Toggle (Binary)
      if (existingCompletion) {
        // Remove completion
        const { error } = await supabase
          .from('habit_completions')
          .delete()
          .eq('habit_id', habitId)
          .eq('completed_at', dateStr);

        if (!error) {
          setCompletions(completions.filter(c => !(c.habitId === habitId && c.date === dateStr)));
          const newPoints = Math.max(0, points - 10);
          updatePoints(newPoints);

          // Also check badges (e.g. if I removed a completion, I might lose a badge? 
          // Usually we don't revoke badges, but let's just pass data for consistency if needed later)
          checkAndAwardBadges(completions, newPoints);
        }
      } else {
        // Add completion
        const { data, error } = await supabase
          .from('habit_completions')
          .insert([{
            user_id: session.user.id,
            habit_id: habitId,
            completed_at: dateStr
          }])
          .select();

        if (data) {
          const newCompletion = { ...data[0], habitId: data[0].habit_id, date: data[0].completed_at };
          const newCompletionsStart = [...completions, newCompletion];
          setCompletions(newCompletionsStart);

          const newPoints = points + 10;
          updatePoints(newPoints);

          checkAndAwardBadges(newCompletionsStart, newPoints);
        }
      }
    }
  };

  const updateBadges = async (newBadges) => {
    setBadges(newBadges);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ badges: newBadges })
        .eq('id', session.user.id);

      if (error) {
        // If column missing, just ignore
        console.warn("Could not save badges", error);
      }
    } catch (err) {
      console.error('Error updating badges:', err);
    }
  };

  const checkAndAwardBadges = (currentCompletions, currentPoints) => {
    // 1. Calculate Streak
    const uniqueDates = [...new Set(currentCompletions.map(c => c.date))].sort().reverse();
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

    let streak = 0;
    if (uniqueDates.length > 0) {
      let lastDate = uniqueDates[0];
      // If the most recent completion is today or yesterday, streak is alive
      if (lastDate === today || lastDate === yesterday) {
        streak = 1;
        for (let i = 1; i < uniqueDates.length; i++) {
          const prevDate = new Date(uniqueDates[i - 1]);
          const currDate = new Date(uniqueDates[i]); // Older date

          const diffTime = Math.abs(prevDate - currDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays === 1) {
            streak++;
          } else {
            break;
          }
        }
      }
    }

    // 2. Determine earned badges
    const newBadges = [...badges];
    let badgesChanged = false;
    let pointsToAdd = 0;

    const awardBadge = (badgeId) => {
      if (!newBadges.includes(badgeId)) {
        newBadges.push(badgeId);
        badgesChanged = true;
        const badgeDef = BADGE_DEFINITIONS.find(b => b.id === badgeId);
        if (badgeDef && badgeDef.points > 0) {
          pointsToAdd += badgeDef.points;
        }
      }
    };

    // Streak Badges
    if (streak >= 3) awardBadge('streak_3');
    if (streak >= 7) awardBadge('streak_7');
    if (streak >= 14) awardBadge('streak_14');
    if (streak >= 30) awardBadge('streak_30');

    // Century Badge
    if (currentPoints >= 100) awardBadge('century');

    // Perfect Day Badge
    if (currentCompletions.length > 0) {
      // Check ONLY for the date of the last action to be efficient
      const lastActionDate = currentCompletions[currentCompletions.length - 1].date; // array is chronological?
      // Wait, completions state order? Line 82 ordered 'habits' by createdAt.
      // Completions fetch (Line 97) doesn't specify order.
      // But toggleCompletion adds to end.

      // Let's filter completions for "today" (or the updated date passed in currentCompletions if possible?)
      // Actually, let's just check "today" and "yesterday" to cover most cases.
      // Or better: check the count of completions for the date of the *latest* completion added.
      // But `currentCompletions` might not be sorted.

      const completionsByDate = {};
      currentCompletions.forEach(c => {
        completionsByDate[c.date] = (completionsByDate[c.date] || 0) + 1;
      });

      const activeHabitsCount = habits.length; // Simply use all habits
      if (activeHabitsCount > 0) {
        Object.entries(completionsByDate).forEach(([date, count]) => {
          if (count >= activeHabitsCount) {
            awardBadge('perfect_day');
          }
        });
      }
    }

    if (badgesChanged) {
      updateBadges(newBadges);
      if (pointsToAdd > 0) {
        updatePoints(currentPoints + pointsToAdd);
        // If we added points, we might have hit 100. Check Century again?
        // Simpler: Just allow it to trigger next time.
      }
    }
  };

  if (!profile) return <div className="p-8 text-center text-muted-foreground">Carregando perfil...</div>;

  const isPremium = profile.subscription_status === 'active';

  const handleSubscribe = async (priceId) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ price_id: priceId })
      });

      const { url } = await response.json();
      if (url) window.location.href = url;
    } catch (err) {
      console.error("Payment error", err);
      alert("Erro ao iniciar pagamento");
    }
  };


  const currentLevel = Math.floor(points / 100) + 1;


  const handleEditClick = (habit) => {
    setEditingHabit(habit);
    setDialogStep('form');
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingHabit(null);
    setDialogStep('choice');
  };


  const handleTemplateSelect = (template) => {
    // Pre-fill form logic
    setEditingHabit({
      name: template.name,
      category: template.category,
      color: template.color,
      icon: template.icon,
      target: template.target,
      unit: template.unit,
      description: `Meta: ${template.target} ${template.unit}`,
      frequency: { type: 'daily' } // Default
    });
    setDialogStep('form');
  }

  if (!isPremium) {

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-8 p-4">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Escolha seu Plano
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            O SkipLater é uma plataforma exclusiva para membros. Assine para desbloquear seu potencial e construir hábitos duradouros.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 w-full max-w-2xl">
          {/* Mensal */}
          <div className="border rounded-xl p-6 flex flex-col gap-4 hover:border-purple-500 transition-colors shadow-sm">
            <div>
              <h3 className="text-xl font-semibold">Mensal</h3>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-3xl font-bold">R$ 9,90</span>
                <span className="text-muted-foreground">/mês</span>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground flex-1">
              <li className="flex items-center gap-2">✓ Acesso completo</li>
              <li className="flex items-center gap-2">✓ Estatísticas avançadas</li>
              <li className="flex items-center gap-2">✓ Cancelamento flexível</li>
            </ul>
            <Button
              onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_MONTHLY)}
              className="w-full"
            >
              Assinar Mensal
            </Button>
          </div>

          {/* Anual */}
          <div className="border rounded-xl p-6 flex flex-col gap-4 border-purple-200 bg-purple-50/50 dark:bg-purple-900/10 shadow-md relative overflow-hidden">
            <div className="absolute top-3 right-3 bg-purple-600 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
              Melhor Valor
            </div>
            <div>
              <h3 className="text-xl font-semibold">Anual</h3>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-3xl font-bold">R$ 97,00</span>
                <span className="text-muted-foreground">/ano</span>
              </div>
              <p className="text-xs text-green-600 font-medium mt-1">Economize ~18%</p>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground flex-1">
              <li className="flex items-center gap-2">✓ Tudo do plano mensal</li>
              <li className="flex items-center gap-2">✓ Pagamento único anual</li>
              <li className="flex items-center gap-2">✓ Foco no longo prazo</li>
            </ul>
            <Button
              onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_YEARLY)}
              variant="default"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              Assinar Anual
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tracker Content (Premium User) */}
      <ReminderNotifications
        habits={habits}
        completions={completions}
        onToggleCompletion={toggleCompletion}
      />

      <GamificationBar points={points} level={currentLevel} badges={badges} />

      <UpcomingReminders
        habits={habits}
        completions={completions}
        onToggleCompletion={toggleCompletion}
      />



      <div className="flex items-center justify-between">

        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingHabit(null);
            setDialogStep('choice');
          }
        }}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={() => setDialogStep('choice')}>
              <Plus className="w-4 h-4" />
              Adicionar Hábito
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>
                {dialogStep === 'choice' && 'Novo Hábito'}
                {dialogStep === 'library' && 'Biblioteca de Hábitos'}
                {dialogStep === 'form' && (editingHabit?.id ? 'Editar Hábito' : 'Criar Hábito')}
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 pr-2 -mr-2 dialog-scroll p-1">

              {dialogStep === 'choice' && (
                <div className="flex flex-col gap-4 py-4">
                  <Button variant="outline" className="h-24 flex flex-col gap-2 items-center justify-center border-dashed" onClick={() => setDialogStep('library')}>
                    <Sparkles className="w-8 h-8 text-purple-500" />
                    <span className="font-semibold">Escolher da Biblioteca</span>
                    <span className="text-xs text-muted-foreground font-normal">Modelos prontos para começar rápido</span>
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t"></span></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Ou</span></div>
                  </div>

                  <Button variant="outline" className="h-24 flex flex-col gap-2 items-center justify-center border-dashed" onClick={() => { setEditingHabit(null); setDialogStep('form'); }}>
                    <Plus className="w-8 h-8 text-muted-foreground" />
                    <span className="font-semibold">Criar do Zero</span>
                    <span className="text-xs text-muted-foreground font-normal">Personalize totalmente seu hábito</span>
                  </Button>
                </div>
              )}

              {dialogStep === 'library' && (
                <div className="py-2 space-y-6">
                  {['Saúde', 'Movimento', 'Mente', 'Produtividade'].map(category => (
                    <div key={category} className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">{category}</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {SUGGESTED_HABITS.filter(h => h.category === category).map(habit => {
                          const Icon = ICON_MAP[habit.icon];
                          return (
                            <button
                              key={habit.name}
                              onClick={() => handleTemplateSelect(habit)}
                              className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent text-left transition-colors"
                            >
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                <Icon className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="font-medium text-sm">{habit.name}</div>
                                <div className="text-xs text-muted-foreground">{habit.target} {habit.unit}</div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => setDialogStep('choice')} className="w-full">Voltar</Button>
                </div>
              )}

              {dialogStep === 'form' && (
                <HabitForm
                  onSubmit={editingHabit?.id ? updateHabit : addHabit}
                  initialData={editingHabit}
                  onCancel={handleDialogClose}
                />
              )}

            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-md">
          <TabsTrigger value="today" className="gap-2">
            <List className="w-4 h-4" />
            Hoje
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <CalendarIcon className="w-4 h-4" />
            Calendário
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2">
            Stats
          </TabsTrigger>
          <TabsTrigger value="badges" className="gap-2">
            <Award className="w-4 h-4" />
            Conquistas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-6">
          <HorizontalCalendar selectedDate={selectedDate} onSelectDate={setSelectedDate} />
          <HabitList
            habits={habits}
            completions={completions}
            onToggleCompletion={toggleCompletion}
            onEdit={handleEditClick}
            onDelete={deleteHabit}
            date={selectedDate}
          />
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <CalendarView
            habits={habits}
            completions={completions}
          />
        </TabsContent>

        <TabsContent value="stats" className="mt-6">
          <StatsView
            habits={habits}
            completions={completions}
          />
        </TabsContent>

        <TabsContent value="badges" className="mt-6">
          <BadgesList earnedBadges={badges} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HabitTracker;
